import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAuthUser, getUserTypeName } from "../admin/navigation/adminNavigation";
import { updateSessionUser } from "../features/auth/authSlice";
import { fetchPartnerUserById } from "../api/partner/partnerApi";

/**
 * Single source of truth for the partner "service type".
 *
 * The backend stores it as one numeric `service_id` on the user (see GET /all/services), with
 * `service_name` as a label. Interior Design is id 1, Property is id 2.
 */
export const PARTNER_SERVICE_IDS = Object.freeze({
  INTERIOR_DESIGN: 1,
  PROPERTY: 2,
});

export const INTERIOR_DESIGN_HOME = "/partner/interior-design";
export const PARTNER_HOME = "/partner/workspace";

// Property creation entry points that an Interior Design partner must not reach.
export const PROPERTY_CREATION_PATHS = ["/property/add-property", "/add-property"];

const toServiceId = (user) => {
  const raw = user?.service_id ?? user?.serviceId ?? user?.service?.id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : 0;
};

const toServiceName = (user) =>
  String(user?.service_name ?? user?.serviceName ?? user?.service?.name ?? "")
    .trim()
    .toLowerCase();

/** True once the user object carries any service information (id or name). */
export const hasPartnerServiceInfo = (user) =>
  Boolean(user) && (toServiceId(user) > 0 || toServiceName(user) !== "");

/**
 * Interior Design check: numeric id first, then a case-insensitive name fallback.
 * Accepts a user object; returns false for null/undefined.
 */
export const isInteriorDesignPartner = (user) => {
  if (!user) return false;
  const id = toServiceId(user);
  if (id > 0) return id === PARTNER_SERVICE_IDS.INTERIOR_DESIGN;
  return toServiceName(user).includes("interior");
};

/**
 * Resolve the service type from an auth state (`state.auth`).
 *  - "unknown":  no user yet, or the user has no service info and `waitForService` is set
 *                (render neither menu, so the wrong one never flashes)
 *  - "interior": Interior Design partner (catalog-only experience)
 *  - "standard": any other user/partner (existing experience, unchanged)
 */
export const resolvePartnerService = (authState, { waitForService = false } = {}) => {
  const user = getAuthUser(authState);
  if (!user) return "unknown";
  if (isInteriorDesignPartner(user)) return "interior";
  if (!hasPartnerServiceInfo(user) && waitForService) return "unknown";
  return "standard";
};

/**
 * Hook form. `isLoading` is true while the user is not available yet.
 */
export const usePartnerService = () => {
  const authState = useSelector((state) => state.auth);
  const kind = resolvePartnerService(authState);
  return {
    kind,
    isLoading: kind === "unknown",
    isInteriorDesign: kind === "interior",
  };
};

/**
 * Like `usePartnerService`, but for UI that must not flash the wrong experience.
 * If the stored session has no service info (older session), the user is fetched once and the
 * service is written back to the session, so sidebar, guards and refreshes all agree.
 * `kind` stays "unknown" until that check is done.
 */
export const useResolvedPartnerService = () => {
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const user = getAuthUser(authState);
  const isPartner = String(getUserTypeName(authState) || "").toUpperCase() === "PARTNER";
  const token = authState?.user?.accessToken || authState?.user?.token;
  const userId = user?.id;
  const needsLookup = Boolean(user) && isPartner && !hasPartnerServiceInfo(user) && Boolean(token && userId);
  const [checkedUserId, setCheckedUserId] = useState(null);

  useEffect(() => {
    if (!needsLookup || checkedUserId === userId) return undefined;
    let cancelled = false;
    fetchPartnerUserById(token, userId)
      .then((res) => {
        const fresh = res?.user;
        if (!cancelled && hasPartnerServiceInfo(fresh)) {
          dispatch(
            updateSessionUser({ service_id: fresh.service_id, service_name: fresh.service_name })
          );
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCheckedUserId(userId);
      });
    return () => {
      cancelled = true;
    };
  }, [needsLookup, checkedUserId, token, userId, dispatch]);

  const waiting = needsLookup && checkedUserId !== userId;
  const kind = resolvePartnerService(authState, { waitForService: waiting });
  return {
    kind,
    isLoading: kind === "unknown",
    isInteriorDesign: kind === "interior",
    isPartner,
  };
};
